import { css } from '@emotion/react'
import { noop } from 'lodash-es'
import { useId } from 'react'

import { Toggle } from '@/component/atoms/Toggle'
import { default as AutoMarkableGuide } from '@/component/molecules/AutoMarkableGuide'

type Props = {
  checked: boolean
  onChange: (checked: boolean) => void
  /** 자동채점 안내 아이콘 노출 여부 */
  showGuide?: boolean
  guideType?: 'makeWorksheet' | 'makeWorkbook'
  /** 안내 아이콘을 라벨 앞/뒤 중 어디에 둘지 */
  guidePosition?: 'start' | 'end'
  showLabel?: boolean
  /** inline: 라벨·토글을 붙여 묶음 / spread: (라벨·안내) 좌측, 토글 우측 양끝 정렬 행 */
  layout?: 'inline' | 'spread'
  className?: string
}

/**
 * 자동채점(자동 채점 가능 문제만) ON/OFF 토글. 문제 타입/답안 타입 등 옵션에서 공용으로 사용한다.
 */
const AutoScoringToggle = ({
  checked,
  onChange,
  showGuide = true,
  guideType = 'makeWorksheet',
  guidePosition = 'start',
  showLabel = true,
  layout = 'inline',
  className,
}: Props) => {
  const id = useId()

  const guide = showGuide ? <AutoMarkableGuide type={guideType} variant="icon" /> : null
  const label = showLabel ? <span css={[S.label, checked && S.labelOn]}>자동채점</span> : null

  if (layout === 'spread') {
    return (
      <div css={S.row} className={className}>
        <div css={S.group}>
          {guidePosition === 'start' && guide}
          {label}
          {guidePosition === 'end' && guide}
        </div>
        <Toggle
          id={id}
          onClick={() => onChange(!checked)}
          checked={checked}
          aria-label="자동채점"
        />
      </div>
    )
  }

  return (
    <div css={S.wrap} className={className}>
      {guidePosition === 'start' && guide}
      <div css={S.cluster} onClick={() => onChange(!checked)}>
        {label}
        <Toggle id={id} onClick={noop} checked={checked} aria-label="자동채점" />
      </div>
      {guidePosition === 'end' && guide}
    </div>
  )
}

export default AutoScoringToggle

const S = {
  wrap: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  cluster: css`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  `,
  row: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `,
  group: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  label: css`
    color: var(--Colors-Gray-500);
    font-weight: 700;
  `,
  labelOn: css`
    color: var(--Colors-Sky-400);
  `,
}
